/**
 * Job checklists, photos and completion reports: the pure rules.
 *
 * The database does the automatic copy (public._copy_checklist_template_to_job
 * in migration 20261102005644_job_checklists_photos_reports.sql) when a job is
 * created for a service with a default checklist; buildJobChecklistItems() is
 * the same rule for when an owner picks a checklist by hand. Keep the two in
 * step: template order is sort_order, then created_at, then id, renumbered
 * 0..n-1.
 */
import type { PlanKey } from '@/lib/plan-limits';

export interface ChecklistTemplateItem {
  id: string;
  label: string;
  room: string | null;
  sort_order: number;
  created_at?: string | null;
}

export interface JobChecklistItem {
  id: string;
  job_id: string;
  template_item_id: string | null;
  label: string;
  room: string | null;
  sort_order: number;
  done: boolean;
  done_by: string | null;
  done_by_name: string | null;
  done_at: string | null;
  note: string | null;
}

export interface NewJobChecklistItem {
  job_id: string;
  template_item_id: string | null;
  label: string;
  room: string | null;
  sort_order: number;
}

const cleanRoom = (room: string | null | undefined) => {
  const trimmed = (room ?? '').trim();
  return trimmed ? trimmed : null;
};

function compareTemplateItems(a: ChecklistTemplateItem, b: ChecklistTemplateItem): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  const ac = a.created_at ?? '';
  const bc = b.created_at ?? '';
  if (ac !== bc) return ac < bc ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** The rows to insert when a template is copied onto a job. Blank labels are skipped. */
export function buildJobChecklistItems(
  jobId: string,
  templateItems: ChecklistTemplateItem[],
  startAt = 0,
): NewJobChecklistItem[] {
  return [...templateItems]
    .sort(compareTemplateItems)
    .filter((item) => item.label.trim().length > 0)
    .map((item, index) => ({
      job_id: jobId,
      template_item_id: item.id,
      label: item.label.trim(),
      room: cleanRoom(item.room),
      sort_order: startAt + index,
    }));
}

export interface ChecklistProgress {
  done: number;
  total: number;
  /** Whole-number percentage, 0 when there are no items. */
  percent: number;
  complete: boolean;
  /** "12/15". */
  label: string;
}

export function checklistProgress(items: Pick<JobChecklistItem, 'done'>[] | null | undefined): ChecklistProgress {
  const total = items?.length ?? 0;
  const done = items?.filter((item) => item.done).length ?? 0;
  const percent = total === 0 ? 0 : Math.floor((done / total) * 100);
  return { done, total, percent, complete: total > 0 && done === total, label: `${done}/${total}` };
}

/** Progress for many jobs at once, from rows of { job_id, done }. */
export function progressByJob(rows: { job_id: string; done: boolean }[] | null | undefined): Map<string, ChecklistProgress> {
  const grouped = new Map<string, { done: boolean }[]>();
  for (const row of rows ?? []) {
    const list = grouped.get(row.job_id) ?? [];
    list.push(row);
    grouped.set(row.job_id, list);
  }
  const result = new Map<string, ChecklistProgress>();
  for (const [jobId, list] of grouped) result.set(jobId, checklistProgress(list));
  return result;
}

export interface RoomGroup<T> {
  /** null for items without a room. */
  room: string | null;
  items: T[];
}

/**
 * Group items by room, keeping rooms in the order they first appear (by
 * sort_order) and items in sort_order within a room.
 */
export function groupByRoom<T extends { room: string | null; sort_order: number }>(items: T[]): RoomGroup<T>[] {
  const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
  const groups: RoomGroup<T>[] = [];
  const index = new Map<string, RoomGroup<T>>();
  for (const item of sorted) {
    const room = cleanRoom(item.room);
    const key = room?.toLowerCase() ?? '';
    let group = index.get(key);
    if (!group) {
      group = { room, items: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}

/** Photos and completion reports are on Team and Business (and the free trial). */
export function canUsePhotoReports(plan: PlanKey | null | undefined): boolean {
  return plan === 'pro' || plan === 'business';
}

/** Fit a width × height inside a square of maxSide, never upscaling. */
export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0 };
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** "<org>/<job>/<uuid>.jpg": the storage path the job-photos policies expect. */
export function jobPhotoPath(orgId: string, jobId: string, fileId: string, ext = 'jpg'): string {
  return `${orgId}/${jobId}/${fileId}.${ext}`;
}

/** Parse a template editor textarea: one item per line, "Room: item" sets the room. */
export function parseItemLines(text: string, currentRoom: string | null = null): { label: string; room: string | null }[] {
  const out: { label: string; room: string | null }[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim().replace(/^[-*•]\s*/, '');
    if (!line) continue;
    const match = line.match(/^([^:]{1,80}):\s*(.+)$/);
    if (match) out.push({ room: match[1].trim(), label: match[2].trim() });
    else out.push({ room: cleanRoom(currentRoom), label: line });
  }
  return out;
}

/** Ready-made lists an owner can start from (and edit). */
export const STARTER_CHECKLISTS: { name: string; description: string; items: { room: string | null; label: string }[] }[] = [
  {
    name: 'Regular clean',
    description: 'Weekly or fortnightly home clean',
    items: [
      { room: 'Kitchen', label: 'Wipe worktops, hob and splashbacks' },
      { room: 'Kitchen', label: 'Clean sink and taps' },
      { room: 'Kitchen', label: 'Wipe cupboard fronts and appliance doors' },
      { room: 'Bathroom', label: 'Clean toilet, basin and bath or shower' },
      { room: 'Bathroom', label: 'Polish mirrors and taps' },
      { room: 'Living areas', label: 'Dust surfaces and skirting boards' },
      { room: 'Bedrooms', label: 'Dust surfaces and make beds' },
      { room: 'Throughout', label: 'Hoover carpets and rugs' },
      { room: 'Throughout', label: 'Mop hard floors' },
      { room: 'Throughout', label: 'Empty bins' },
    ],
  },
  {
    name: 'End of tenancy',
    description: 'Deep clean ready for the inventory check',
    items: [
      { room: 'Kitchen', label: 'Clean inside oven, hob and extractor' },
      { room: 'Kitchen', label: 'Clean inside fridge and freezer' },
      { room: 'Kitchen', label: 'Clean inside and outside all cupboards' },
      { room: 'Bathroom', label: 'Descale shower, taps and screen' },
      { room: 'Bathroom', label: 'Clean grout and sealant' },
      { room: 'Throughout', label: 'Wash skirting boards, doors and frames' },
      { room: 'Throughout', label: 'Clean switches and sockets' },
      { room: 'Throughout', label: 'Clean inside windows and sills' },
      { room: 'Throughout', label: 'Hoover and mop all floors' },
      { room: 'Throughout', label: 'Check every room against the inventory' },
    ],
  },
];
